import { useState } from "react";
import { useFetch, apiPost, apiPut } from "@/lib/shieldx";
import { PageHeader, Loader, Panel } from "@/components/shieldx/Primitives";
import { DemoTag } from "@/components/shieldx/Badges";
import { ScrollText, Plus, Power } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const FIELDS = ["Source Risk", "Asset", "Category", "AI Agent Action", "Scope", "Risk"];
const OPS = ["=", "≠", ">", "<"];
const ACTIONS = ["BLOCK", "DENY", "ALLOW", "WARN", "RATE LIMIT", "ISOLATE", "ALERT", "LOG", "REVOKE CREDENTIALS", "REQUIRE HUMAN APPROVAL"];

export default function Policies() {
  const { data, loading, reload } = useFetch("/policies");
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [conds, setConds] = useState([{ field: "Source Risk", op: "=", value: "HIGH" }]);
  const [acts, setActs] = useState(["BLOCK"]);
  if (loading || !data) return <Loader label="Loading policies" />;

  const toggle = async (p) => { await apiPut(`/policies/${p.id}`, { enabled: !p.enabled }); reload(); };
  const addCond = () => setConds([...conds, { field: "Asset", op: "=", value: "PRODUCTION" }]);
  const setCond = (i, k, v) => setConds(conds.map((c, idx) => idx === i ? { ...c, [k]: v } : c));
  const toggleAct = (a) => setActs(acts.includes(a) ? acts.filter((x) => x !== a) : [...acts, a]);
  const create = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    await apiPost("/policies", { name, conditions: conds, actions: acts, enabled: true, scope: "Organization" });
    toast.success("Policy created"); setOpen(false); setName(""); setConds([{ field: "Source Risk", op: "=", value: "HIGH" }]); setActs(["BLOCK"]); reload();
  };

  return (
    <div>
      <PageHeader icon={ScrollText} title="Policy Builder"
        subtitle="Visual IF / THEN authorization logic. Policies decide ALLOW, DENY, REVIEW and containment for every sensitive action."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button data-testid="new-policy" className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500"><Plus className="h-4 w-4" /> New Policy</button>
            </DialogTrigger>
            <DialogContent className="max-w-lg border-slate-800 bg-slate-900 text-slate-100">
              <DialogHeader><DialogTitle className="font-display">Create Policy</DialogTitle></DialogHeader>
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="policy-name" placeholder="Policy name"
                className="h-10 w-full rounded-lg border border-slate-800 bg-slate-900/70 px-3 text-sm text-slate-100 outline-none focus:border-blue-500/50" />
              <div>
                <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-blue-400/80">IF</p>
                <div className="space-y-2">
                  {conds.map((c, i) => (
                    <div key={i} className="grid grid-cols-3 gap-2">
                      <select value={c.field} onChange={(e) => setCond(i, "field", e.target.value)} className="h-9 rounded-md border border-slate-800 bg-slate-900/70 px-2 text-xs text-slate-200">{FIELDS.map((f) => <option key={f}>{f}</option>)}</select>
                      <select value={c.op} onChange={(e) => setCond(i, "op", e.target.value)} className="h-9 rounded-md border border-slate-800 bg-slate-900/70 px-2 text-xs text-slate-200">{OPS.map((o) => <option key={o}>{o}</option>)}</select>
                      <input value={c.value} onChange={(e) => setCond(i, "value", e.target.value)} className="h-9 rounded-md border border-slate-800 bg-slate-900/70 px-2 text-xs text-slate-200" />
                    </div>
                  ))}
                </div>
                <button onClick={addCond} className="mt-2 text-xs text-blue-400 hover:text-blue-300">+ Add condition</button>
              </div>
              <div>
                <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-emerald-400/80">THEN</p>
                <div className="flex flex-wrap gap-1.5">
                  {ACTIONS.map((a) => (
                    <button key={a} onClick={() => toggleAct(a)} className={`rounded-md border px-2.5 py-1 text-xs ${acts.includes(a) ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-slate-700 bg-slate-800/60 text-slate-400"}`}>{a}</button>
                  ))}
                </div>
              </div>
              <button onClick={create} data-testid="policy-save" className="mt-2 rounded-lg bg-blue-600 py-2.5 text-sm font-semibold text-white hover:bg-blue-500">Create Policy</button>
            </DialogContent>
          </Dialog>
        } />
      <DemoTag />
      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        {data.map((p) => (
          <Panel key={p.id} title={p.name} desc={p.scope}
            actions={
              <button onClick={() => toggle(p)} data-testid={`policy-toggle-${p.id}`}
                className={`flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium ${p.enabled ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-slate-700 bg-slate-800/60 text-slate-400"}`}>
                <Power className="h-3.5 w-3.5" /> {p.enabled ? "Enabled" : "Disabled"}
              </button>
            }>
            <div className="space-y-2">
              <p className="font-mono text-[10px] uppercase tracking-widest text-blue-400/80">IF</p>
              {(p.conditions || []).map((c, i) => (
                <div key={i} className="rounded-md border border-slate-800 bg-slate-900/60 px-3 py-1.5 font-mono text-xs text-slate-300">
                  {c.field} <span className="text-blue-400">{c.op}</span> {c.value}
                </div>
              ))}
              <p className="pt-1 font-mono text-[10px] uppercase tracking-widest text-emerald-400/80">THEN</p>
              <div className="flex flex-wrap gap-1.5">
                {(p.actions || []).map((a) => <span key={a} className="rounded border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 font-mono text-xs text-emerald-300">{a}</span>)}
              </div>
            </div>
          </Panel>
        ))}
      </div>
    </div>
  );
}
